import { TVOverlay } from "@/components/tv-overlay";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface StatsCardProps {
  value: string;
  label: string;
  dotColor: string;
  textColor: string;
  bgColor: string;
  isLoading?: boolean;
  position?: "first" | "middle" | "last" | "single";
}

export function StatsCard({
  value,
  label,
  dotColor,
  textColor,
  bgColor,
  isLoading = false,
  position = "single",
}: StatsCardProps) {
  // Split the value string to separate the number from any letter suffix (like k, M, B)
  const splitValue = () => {
    // Match any number (including decimal points) followed by non-numeric characters
    const match = value.match(/^([\d,.]+)(.*)$/);
    if (match) {
      return {
        number: match[1], // The numeric part
        suffix: match[2], // The suffix part (k, M, B, etc.)
      };
    }
    return { number: value, suffix: "" };
  };

  const { number, suffix } = splitValue();

  // Always use rounded corners for TV-style look
  const getRoundedClass = () => {
    return "rounded-md";
  };

  return (
    <Card
      className={`group relative flex h-24 flex-col items-center justify-center overflow-hidden border-none p-3 transition-[box-shadow,border-color] duration-300 ease-out ${getRoundedClass()}`}
      style={{
        background: `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}ee 100%)`,
        boxShadow: `
          inset 0 2px 10px rgba(0, 0, 0, 0.8),
          inset 0 -1px 0 ${dotColor}30,
          0 1px 0 rgba(255, 255, 255, 0.05)
        `.trim(),
        border: `1px solid rgba(0, 0, 0, 0.8)`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `
          inset 0 2px 12px rgba(0, 0, 0, 0.9),
          inset 0 -1px 0 ${dotColor}50,
          0 1px 0 rgba(255, 255, 255, 0.08),
          0 0 20px ${dotColor}15
        `.trim();
        e.currentTarget.style.borderColor = `rgba(0, 0, 0, 0.9)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = `
          inset 0 2px 10px rgba(0, 0, 0, 0.8),
          inset 0 -1px 0 ${dotColor}30,
          0 1px 0 rgba(255, 255, 255, 0.05)
        `.trim();
        e.currentTarget.style.borderColor = `rgba(0, 0, 0, 0.8)`;
      }}
    >
      {/* TV Overlay Effect */}
      <TVOverlay intensity="light" />

      {isLoading ? (
        <Skeleton className="h-16 w-20" />
      ) : (
        <div className="flex flex-col items-center gap-1.5">
          <div
            className="w-fit font-pressStart2P text-lg leading-tight tracking-wider transition-transform duration-300 ease-out group-hover:scale-105"
            style={{
              color: textColor,
              textShadow: `0 0 10px ${textColor}80, 0 0 20px ${textColor}40`,
            }}
          >
            {number}
            <span className="text-base">{suffix}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="size-1 transition-colors duration-1000"
              style={{
                backgroundColor: dotColor,
                animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
              }}
            />
            <div className="font-pressStart2P text-[7px] uppercase tracking-widest text-muted-foreground transition-colors duration-300 group-hover:text-white">
              {label}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
